import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, Treemap } from "recharts";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtPct, fmtSigned } from "@/lib/format";

const MODES = [{ key: "subs", label: "Sub-portfolios" }, { key: "stocks", label: "Stocks" }];
const PERIODS = [{ key: "1D", label: "Today" }, { key: "1W", label: "Week" }, { key: "1M", label: "Month" }, { key: "YTD", label: "YTD" }];
const TITLES = { "1D": "Today's Heatmap", "1W": "Past Week Heatmap", "1M": "Past Month Heatmap", YTD: "Year-to-Date Heatmap" };
// % move at which colour reaches full strength, per period
const SATURATE = { "1D": 3, "1W": 6, "1M": 10, YTD: 25 };

const stored = (k, d) => { try { return localStorage.getItem(k) || d; } catch { return d; } };
const store = (k, v) => { try { localStorage.setItem(k, v); } catch { /* storage unavailable */ } };

function tileColor(pct, sat) {
    if (pct == null || Math.abs(pct) < 0.005) return "hsl(var(--muted-foreground) / 0.35)";
    const a = 0.6 + Math.min(Math.abs(pct) / sat, 1) * 0.4;
    return pct > 0 ? `hsl(var(--gain) / ${a})` : `hsl(var(--loss) / ${a})`;
}

function Tile({ x, y, width, height, name, pct, dollar, mask, onOpen, sat }) {
    if (!name || width <= 0 || height <= 0) return null;
    const big = width > 90 && height > 46;
    return (
        <g data-testid={`heatmap-tile-${name}`} onClick={onOpen ? () => onOpen(name) : undefined} style={onOpen ? { cursor: "pointer" } : undefined}>
            <title>{`${name}: ${fmtPct(pct)} (${mask(fmtSigned(dollar))})`}</title>
            <rect x={x} y={y} width={width} height={height} rx={6} fill={tileColor(pct, sat)} stroke="hsl(var(--background))" strokeWidth={3} />
            {width > 44 && height > 22 && (
                <text x={x + 8} y={y + (big ? 20 : height / 2 + 4)} fill="#fff" fontSize={big ? 13 : 11} fontWeight={600}>
                    {name.length * 7 > width - 12 ? `${name.slice(0, Math.max(3, Math.floor((width - 12) / 7)))}…` : name}
                </text>
            )}
            {big && <text x={x + 8} y={y + 38} fill="#fff" fontSize={12} fontWeight={500}>{fmtPct(pct)}</text>}
            {big && height > 64 && <text x={x + 8} y={y + 55} fill="#fff" fillOpacity={0.85} fontSize={11}>{mask(fmtSigned(dollar))}</text>}
            {!big && width > 44 && height > 48 && <text x={x + 8} y={y + height / 2 + 18} fill="#fff" fontSize={10}>{fmtPct(pct)}</text>}
        </g>
    );
}

function Seg({ options, value, onChange, prefix }) {
    return (
        <div className="inline-flex rounded-lg bg-muted p-0.5" data-testid={`${prefix}-toggle`}>
            {options.map((o) => (
                <button key={o.key} onClick={() => onChange(o.key)} data-testid={`${prefix}-${o.key}`}
                    className={`px-2 py-0.5 text-[11px] font-medium rounded-md transition-colors ${value === o.key ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {o.label}
                </button>
            ))}
        </div>
    );
}

// Group positions by sub-portfolio or by ticker (a stock held in several subs becomes one tile)
function buildTiles(rows, mode) {
    const groups = {};
    for (const r of rows || []) {
        const key = mode === "stocks" ? r.ticker : r.sub_portfolio;
        const g = (groups[key] ||= { name: key, dollar: 0, prev: 0 });
        g.dollar += r.dollar;
        g.prev += r.start_value;
    }
    const tiles = Object.values(groups).map((g) => ({ ...g, pct: g.prev ? (g.dollar / g.prev) * 100 : null }));
    const allFlat = tiles.every((t) => Math.abs(t.dollar) < 0.01);
    const totalAbs = tiles.reduce((t, r) => t + Math.abs(r.dollar), 0);
    // floor each tile so near-flat entries stay visible (smaller floor when there are many stocks)
    const floor = mode === "stocks" ? 0.02 : 0.08;
    return tiles.map((t) => ({ ...t, size: allFlat ? 1 : Math.max(Math.abs(t.dollar), totalAbs * floor) })).sort((a, b) => b.size - a.size);
}

/** Moves over a period: tile size = size of the $ move, colour = % move. Sub-portfolio or stock view. */
export function SubPortfolioHeatmap({ sub = "ALL", mask, height = 200 }) {
    const navigate = useNavigate();
    const [mode, setMode] = useState(() => stored("moringa-heatmap-mode", "subs"));
    const [period, setPeriod] = useState(() => stored("moringa-heatmap-period", "1D"));
    const q = useQuery({ queryKey: ["heatmap", sub, period], queryFn: async () => (await api.get(`/portfolio/heatmap?period=${period}&sub=${encodeURIComponent(sub)}`)).data });
    const data = buildTiles(q.data?.rows, mode);
    const onOpen = mode === "stocks" ? (tk) => navigate(`/security/${tk}`) : undefined;
    return (
        <Card className="p-4" data-testid="subportfolio-heatmap">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h2 className="font-heading text-base font-semibold" data-testid="heatmap-title">{TITLES[period]}</h2>
                <div className="flex flex-wrap items-center gap-1.5">
                    <Seg options={PERIODS} value={period} onChange={(v) => { setPeriod(v); store("moringa-heatmap-period", v); }} prefix="heatmap-period" />
                    <Seg options={MODES} value={mode} onChange={(v) => { setMode(v); store("moringa-heatmap-mode", v); }} prefix="heatmap-mode" />
                </div>
            </div>
            {q.isLoading ? <Skeleton className="rounded-lg" style={{ height }} /> : data.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No price data for this period yet.</p> : (
                <ResponsiveContainer width="100%" height={height}>
                    <Treemap key={mode + period} data={data} dataKey="size" isAnimationActive={false} content={<Tile mask={mask} onOpen={onOpen} sat={SATURATE[period]} />} />
                </ResponsiveContainer>
            )}
            <p className="hidden sm:block text-[10px] text-muted-foreground mt-1.5">Size = $ move · colour = % change over the period, on current shares{mode === "stocks" ? " · tap a stock to open it" : ""}</p>
        </Card>
    );
}
